import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { CheckCircle, X, Clock, AlertCircle } from "lucide-react";
import "./Pyment.css";

const QR_EXPIRE_SECONDS = 5 * 60; // 5 minutes

const PaymentQRModal = ({
  open,
  data,
  status,
  payment_status,
  onClose
}) => {
  const [timeLeft, setTimeLeft] = useState(QR_EXPIRE_SECONDS);
  const [showSuccess, setShowSuccess] = useState(false);
  const dispatch = useDispatch();

  /* ---------------- TIMER (5 MIN) ---------------- */
  useEffect(() => {
    if (!open) return;

    setTimeLeft(QR_EXPIRE_SECONDS);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  /* ---------------- AUTO CLOSE ON SUCCESS ---------------- */
useEffect(() => {
  if (payment_status === "paid" && !showSuccess) {
    setShowSuccess(true);

    const timer = setTimeout(() => {
      onClose(); // 👈 now this REALLY closes the modal
    }, 3000);

    return () => clearTimeout(timer);
  }
}, [payment_status, showSuccess, onClose]);



  /* ---------------- AUTO CLOSE ON QR EXPIRY ---------------- */
  useEffect(() => {
    if (timeLeft === 0 && status === "pending") {
      setTimeout(() => {
        onClose();
      }, 1500);
    }
  }, [timeLeft, status, onClose]);

  /* ---------------- RESET SUCCESS ON CLOSE ---------------- */
  useEffect(() => {
    if (!open) {
      setShowSuccess(false);
    }
  }, [open]);

  // Debug logging
  useEffect(() => {
    console.log('PaymentQRModal State:', {
      open,
      status,
      payment_status,
      showSuccess,
      hasData: !!data
    });
  }, [open, status, payment_status, showSuccess, data]);

  if (!open || !data) return null;

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const isExpiringSoon = timeLeft <= 60;

  return (
    <div className="payment-overlay">
      <div className={`payment-modal-enhanced ${showSuccess ? 'success-state' : ''}`}>

        {/* CLOSE BUTTON - Only show when not success */}
        {!showSuccess && (
          <button className="close-btn-enhanced" onClick={onClose}>
            <X size={24} />
          </button>
        )}

        {/* ---------------- SUCCESS STATE ---------------- */}
        {(showSuccess || payment_status === "paid") && (
          <div className="success-animation-container">
            <div className="success-checkmark-wrapper">
              <CheckCircle size={80} className="success-checkmark" />
              <div className="success-ripple"></div>
              <div className="success-ripple delay-1"></div>
            </div>
            <h3 className="success-title">Payment Successful!</h3>
            <p className="success-subtitle">Your slot has been booked successfully</p>
              {/* ✅ MANUAL CLOSE BUTTON */}
    <button
      onClick={onClose}
      className="success-close-btn"
    >
      Done
    </button>
            <div className="success-confetti">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="confetti" style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ec4899'][Math.floor(Math.random() * 4)]
                }}></div>
              ))}
            </div>
          </div>
        )}

        {/* ---------------- PENDING STATE ---------------- */}
        {!showSuccess && status === "pending" && payment_status === "unpaid" && timeLeft > 0 && (
          <>
            <div className="modal-header-enhanced">
              <div className="header-icon-wrapper">
                <Clock size={28} className="header-icon" />
              </div>
              <h3 className="modal-title-enhanced">Scan QR to Pay</h3>
              <p className="modal-subtitle">Complete your payment to confirm booking</p>
            </div>

            <div className="qr-container-enhanced">
              <div className="qr-wrapper">
                <img
                  src={data.qrCode}
                  alt="Payment QR"
                  className="qr-image-enhanced"
                />
                <div className="qr-border"></div>
              </div>
              
              <div className="scanning-animation">
                <div className="scan-line"></div>
              </div>
            </div>

            <div className={`timer-container ${isExpiringSoon ? 'expiring' : ''}`}>
              <Clock size={18} className="timer-icon" />
              <span className="timer-text">
                Expires in <strong>{formatTime(timeLeft)}</strong>
              </span>
            </div>

            <div className="payment-instructions">
              <div className="instruction-step">
                <div className="step-number">1</div>
                <p>Open your payment app</p>
              </div>
              <div className="instruction-step">
                <div className="step-number">2</div>
                <p>Scan the QR code above</p>
              </div>
              <div className="instruction-step">
                <div className="step-number">3</div>
                <p>Complete the payment</p>
              </div>
            </div>

            <div className="processing-indicator">
              <div className="pulse-dot"></div>
              <span>Waiting for payment confirmation...</span>
            </div>
          </>
        )}

        {/* ---------------- EXPIRED STATE ---------------- */}
        {!showSuccess && timeLeft === 0 && status === "pending" && (
          <div className="expired-container">
            <AlertCircle size={60} className="expired-icon" />
            <h3 className="expired-title">QR Code Expired</h3>
            <p className="expired-message">
              The payment window has closed. Please try booking again to generate a new QR code.
            </p>
          </div>
        )}

      </div>

      <style>{`
        .payment-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .payment-modal-enhanced {
          background: white;
          border-radius: 24px;
          padding: 32px;
          max-width: 440px;
          width: 90%;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          max-height: 90vh;
          overflow-y: auto;
        }

        .payment-modal-enhanced.success-state {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        @keyframes slideUp {
          from {
            transform: translateY(40px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .close-btn-enhanced {
          position: absolute;
          top: 16px;
          right: 16px;
          background: #f3f4f6;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          color: #6b7280;
          z-index: 10;
        }

        .close-btn-enhanced:hover {
          background: #e5e7eb;
          transform: scale(1.1);
          color: #374151;
        }

        /* SUCCESS ANIMATION */
        .success-animation-container {
          text-align: center;
          padding: 40px 20px;
          position: relative;
          overflow: hidden;
        }

        .success-checkmark-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 24px;
        }

        .success-checkmark {
          color: white;
          animation: scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
        }

        @keyframes scaleIn {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .success-ripple {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 80px;
          height: 80px;
          border: 3px solid rgba(255, 255, 255, 0.6);
          border-radius: 50%;
          animation: ripple 1.5s ease-out infinite;
        }

        .success-ripple.delay-1 {
          animation-delay: 0.5s;
        }

        @keyframes ripple {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 0;
          }
        }

        .success-title {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 12px 0;
          animation: slideUp 0.6s ease 0.2s both;
        }

        .success-subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          margin: 0;
          animation: slideUp 0.6s ease 0.3s both;
        }

        .success-confetti {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          top: -10px;
          animation: confettiFall 2s ease-out forwards;
        }

        @keyframes confettiFall {
          to {
            transform: translateY(500px) rotate(720deg);
            opacity: 0;
          }
        }

        /* PENDING STATE */
        .modal-header-enhanced {
          text-align: center;
          margin-bottom: 32px;
        }

        .header-icon-wrapper {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          border-radius: 50%;
          margin-bottom: 16px;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .header-icon {
          color: white;
        }

        .modal-title-enhanced {
          font-size: 24px;
          font-weight: 700;
          color: #111827;
          margin: 0 0 8px 0;
        }

        .modal-subtitle {
          font-size: 14px;
          color: #6b7280;
          margin: 0;
        }

        .qr-container-enhanced {
          position: relative;
          margin: 0 auto 24px;
          width: fit-content;
        }

        .qr-wrapper {
          position: relative;
          padding: 20px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }

        .qr-image-enhanced {
          width: 240px;
          height: 240px;
          display: block;
          border-radius: 12px;
        }

        .qr-border {
          position: absolute;
          top: 10px;
          left: 10px;
          right: 10px;
          bottom: 10px;
          border: 3px solid #3b82f6;
          border-radius: 20px;
          pointer-events: none;
        }

        .scanning-animation {
          position: absolute;
          top: 20px;
          left: 20px;
          right: 20px;
          bottom: 20px;
          overflow: hidden;
          border-radius: 12px;
          pointer-events: none;
        }

        .scan-line {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #3b82f6, transparent);
          animation: scan 2s ease-in-out infinite;
        }

        @keyframes scan {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(240px); }
        }

        .timer-container {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          background: #f3f4f6;
          border-radius: 12px;
          margin-bottom: 24px;
          transition: all 0.3s;
        }

        .timer-container.expiring {
          background: #fee2e2;
          animation: pulse 1s ease infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        .timer-icon {
          color: #6b7280;
        }

        .timer-container.expiring .timer-icon {
          color: #dc2626;
        }

        .timer-text {
          font-size: 14px;
          color: #374151;
        }

        .timer-container.expiring .timer-text {
          color: #dc2626;
        }

        .timer-text strong {
          font-weight: 700;
          font-size: 16px;
        }

        .payment-instructions {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
        }

        .instruction-step {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .step-number {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
        }

        .instruction-step p {
          margin: 0;
          color: #374151;
          font-size: 14px;
        }

        .processing-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 16px;
          background: #eff6ff;
          border-radius: 12px;
          font-size: 14px;
          color: #1e40af;
        }

        .pulse-dot {
          width: 8px;
          height: 8px;
          background: #3b82f6;
          border-radius: 50%;
          animation: pulseDot 1.5s ease infinite;
        }

        @keyframes pulseDot {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.5;
          }
        }

        /* EXPIRED STATE */
        .expired-container {
          text-align: center;
          padding: 40px 20px;
        }

        .expired-icon {
          color: #dc2626;
          margin-bottom: 20px;
          animation: shake 0.5s ease;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }

        .expired-title {
          font-size: 22px;
          font-weight: 700;
          color: #dc2626;
          margin: 0 0 12px 0;
        }

        .expired-message {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Scrollbar styling */
        .payment-modal-enhanced::-webkit-scrollbar {
          width: 8px;
        }

        .payment-modal-enhanced::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 4px;
        }

        .payment-modal-enhanced::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }

        .payment-modal-enhanced::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        /* Responsive */
        @media (max-width: 480px) {
          .payment-modal-enhanced {
            padding: 24px;
          }

          .qr-image-enhanced {
            width: 200px;
            height: 200px;
          }

          .modal-title-enhanced {
            font-size: 20px;
          }

          .success-title {
            font-size: 24px;
          }
        }
          .success-close-btn {
  margin-top: 24px;
  padding: 12px 28px;
  border-radius: 999px;
  border: none;
  background: white;
  color: #059669;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.success-close-btn:hover {
  transform: scale(1.05);
  background: #ecfdf5;
}

      `}</style>
    </div>
  );
};

export default PaymentQRModal;